import React, { useMemo, useState } from 'react';
import DeckGL from '@deck.gl/react';
import { useDuckDB } from './hooks/useDuckDB';
import {
  AmenityPremiumChart,
  BarChart,
  DonutUnitType,
  BedroomsBar,
  FloorPriceBar,
  PriceRangeBar,
  AreaDistributionBar,
  AreaPriceLineChart,
  ControlPanel,
  ActiveFilterChips,
  useMapLayers,
  BAN
} from './components';
import { useFilters, whereClause } from './cube/filters';
import {
  H3PriceData,
  NeighborhoodPriceData,
  FloorPriceData,
  AmenityPriceData,
  UnitTypeCountRow,
  BedroomsCountRow,
  PriceRangeCountRow,
  AreaCountRow,
  AreaPriceRow,
  Metrics
} from './types';
import './App.css';

// Initial viewport focused on Fortaleza urban area - matching deck.gl example
const INITIAL_VIEW_STATE = {
  longitude: -38.5167,
  latitude: -3.7827,
  zoom: 10, // Reduced zoom to see more area
  pitch: 30, // Reduced pitch for better visibility
  bearing: 0 // Reset bearing
};

const formatLargeNumber = (num: number | null): string => {
  if (num === null || num === undefined) return 'N/A';
  if (num >= 1_000_000) {
    const millions = num / 1_000_000;
    return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
  }
  if (num >= 1_000) {
    const thousands = num / 1_000;
    return thousands % 1 === 0 ? `${thousands}K` : `${thousands.toFixed(1)}K`;
  }
  return num.toFixed(0);
};

// H3 data now computed dynamically from properties_db.main_cube.cube_rollup_mean (see effects below)

// SQL query to get neighborhood price data for bar chart
const NEIGHBORHOOD_PRICE_QUERY = `
SELECT 
    neighborhood,
    median_price_per_m2,
    count
FROM properties_db.main_marts.price_per_neighborhood
WHERE median_price_per_m2 IS NOT NULL 
  AND median_price_per_m2 > 0
ORDER BY median_price_per_m2 DESC;
`;

// Floor price now computed dynamically from cube (see effects below)

// SQL query to get pre-computed amenity price analysis
const AMENITY_PRICE_QUERY = `
SELECT 
    diff,
    count_with,
    amenity
FROM properties_db.main_marts.amenity_price_analysis
ORDER BY diff DESC;
`;

function App() {
  const { isLoading: dbLoading, isReady, error: dbError, useQuery, query } = useDuckDB();
  const [h3Data, setH3Data] = useState<H3PriceData[]>([]);
  const { isLoading: neighborhoodLoading, error: neighborhoodError } = useQuery<NeighborhoodPriceData>(
    isReady ? NEIGHBORHOOD_PRICE_QUERY : null
  );
  const { data: amenityPriceData, isLoading: amenityPriceLoading, error: amenityPriceError } = useQuery<AmenityPriceData>(
    isReady ? AMENITY_PRICE_QUERY : null
  );

  // Sidebar drawer state for small screens
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  
  React.useEffect(() => {
    const checkScreen = () => {
      const small = window.innerWidth < 1024;
      setIsSmallScreen(small);
      if (!small) setSidebarOpen(false);
    };
    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  // Fixed visualization parameters (controls removed from UI)
  const COVERAGE = 0.9;
  const UPPER_PERCENTILE = 100;
  const [hoveredHex, setHoveredHex] = useState<H3PriceData | null>(null);
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);

  // Calculate opacity based on zoom level
  // Zoom <= 10: opacity = 0.8
  // Zoom >= 13: opacity = 0.2
  // Between: decreases by 0.2 per zoom level
  const opacity = React.useMemo(() => {
    const zoom = viewState.zoom;
    if (zoom <= 10) return 0.8;
    if (zoom >= 13) return 0.2;
    return 0.8 - (zoom - 10) * 0.2;
  }, [viewState.zoom]);

  // Cube-based neighborhoods (mean via SUM/COUNT) + crossfilter
  const { filters, set } = useFilters();
  const [cubeNeighborhoods, setCubeNeighborhoods] = useState<NeighborhoodPriceData[]>([]);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [globalPriceRange, setGlobalPriceRange] = useState<{ min: number; max: number } | null>(null);

  React.useEffect(() => {
    if (!isReady || globalPriceRange) return;
    (async () => {
      const sql = `
        WITH h3_agg AS (
          SELECT h3_7,
                 AVG(sum_price / NULLIF(sum_area, 0)) AS price_per_m2
          FROM properties_db.main_cube.cube_rollup_mean
          WHERE sum_price IS NOT NULL AND sum_area > 0
          GROUP BY h3_7
        )
        SELECT MIN(price_per_m2) AS min_price,
               MAX(price_per_m2) AS max_price
        FROM h3_agg;
      `;
      try {
        const rows = await query<{ min_price: number; max_price: number }>(sql);
        if (rows.length > 0) {
          setGlobalPriceRange({ min: rows[0].min_price, max: rows[0].max_price });
        }
      } catch (e) {
        console.error('Failed to calculate global price range:', e);
      }
    })();
  }, [isReady, globalPriceRange, query]);

  React.useEffect(() => {
    if (!isReady) return;
    (async () => {
      const where = whereClause(filters as any, ['bairros']);
      const sql = `
        SELECT bairro AS neighborhood,
               SUM(property_count) AS count,
               AVG(sum_price / NULLIF(sum_area, 0)) AS median_price_per_m2
        FROM properties_db.main_cube.cube_rollup_mean
        ${where}
        GROUP BY 1
        ORDER BY median_price_per_m2 DESC;
      `;
      try {
        const rows = await query<NeighborhoodPriceData>(sql);
        setCubeNeighborhoods(rows);
      } catch { }
    })();
  }, [isReady, filters, query]);

  // Unit type donut (APARTMENT vs HOME)
  const [unitTypeCounts, setUnitTypeCounts] = useState<UnitTypeCountRow[]>([]);
  React.useEffect(() => {
    if (!isReady) return;
    (async () => {
      const where = whereClause(filters as any, ['unitTypes']);
      const sql = `
        SELECT unit_type, SUM(property_count) AS count
        FROM properties_db.main_cube.cube_rollup_mean
        ${where}
        GROUP BY 1
        ORDER BY count DESC;
      `;
      try {
        const rows = await query<UnitTypeCountRow>(sql);
        setUnitTypeCounts(rows);
      } catch { }
    })();
  }, [isReady, filters, query]);

  // Area distribution bar chart
  const [areaDistribution, setAreaDistribution] = useState<AreaCountRow[]>([]);
  React.useEffect(() => {
    if (!isReady) return;
    (async () => {
      const where = whereClause(filters as any, ['areaTier']);
      const sql = `
        SELECT area_tier, SUM(property_count) AS count
        FROM properties_db.main_cube.cube_rollup_mean
        ${where}
        GROUP BY 1
        ORDER BY 
          CASE area_tier
            WHEN '0-30m²' THEN 1
            WHEN '30-50m²' THEN 2
            WHEN '50-70m²' THEN 3
            WHEN '70-100m²' THEN 4
            WHEN '100-150m²' THEN 5
            WHEN '150-200m²' THEN 6
            WHEN '200-300m²' THEN 7
            WHEN '300+m²' THEN 8
            ELSE 0
          END;
      `;
      try {
        const rows = await query<AreaCountRow>(sql);
        setAreaDistribution(rows);
      } catch { }
    })();
  }, [isReady, filters, query]);

  // Area vs price line chart
  const [areaPriceData, setAreaPriceData] = useState<AreaPriceRow[]>([]);
  React.useEffect(() => {
    if (!isReady) return;
    (async () => {
      const where = whereClause(filters as any);
      const sql = `
        SELECT 
          area_tier,
          SUM(sum_area) / NULLIF(SUM(property_count), 0) AS avg_area,
          AVG(sum_price / NULLIF(sum_area, 0)) AS avg_price_per_m2
        FROM properties_db.main_cube.cube_rollup_mean
        ${where}
        GROUP BY 1
        ORDER BY 
          CASE area_tier
            WHEN '0-30m²' THEN 1
            WHEN '30-50m²' THEN 2
            WHEN '50-70m²' THEN 3
            WHEN '70-100m²' THEN 4
            WHEN '100-150m²' THEN 5
            WHEN '150-200m²' THEN 6
            WHEN '200-300m²' THEN 7
            WHEN '300+m²' THEN 8
            ELSE 0
          END;
      `;
      try {
        const rows = await query<AreaPriceRow>(sql);
        setAreaPriceData(rows);
      } catch { }
    })();
  }, [isReady, filters, query]);

  // Bedrooms bar (by bedrooms_bin)
  const [bedroomsCounts, setBedroomsCounts] = useState<BedroomsCountRow[]>([]);
  React.useEffect(() => {
    if (!isReady) return;
    (async () => {
      const where = whereClause(filters as any, ['bedrooms']);
      const sql = `
        SELECT bedrooms_bin, SUM(property_count) AS count
        FROM properties_db.main_cube.cube_rollup_mean
        ${where}
        GROUP BY 1
        ORDER BY 1;
      `;
      try {
        const rows = await query<BedroomsCountRow>(sql);
        setBedroomsCounts(rows);
      } catch { }
    })();
  }, [isReady, filters, query]);

  // Price range bar (by price_m2_bucket)
  const [priceRangeCounts, setPriceRangeCounts] = useState<PriceRangeCountRow[]>([]);
  React.useEffect(() => {
    if (!isReady) return;
    (async () => {
      const where = whereClause(filters as any, ['priceRange']);
      const sql = `
        SELECT price_m2_bucket, SUM(property_count) AS count
        FROM properties_db.main_cube.cube_rollup_mean
        ${where}
        GROUP BY price_m2_bucket
        ORDER BY 1;
      `;
      try {
        const rows = await query<PriceRangeCountRow>(sql);
        setPriceRangeCounts(rows);
      } catch { }
    })();
  }, [isReady, filters, query]);

  // Metrics (BANs)
  React.useEffect(() => {
    if (!isReady) return;
    (async () => {
      const where = whereClause(filters as any);
      const sql = `
        SELECT
          SUM(property_count) AS total_properties,
          AVG(sum_price / NULLIF(sum_area, 0)) AS avg_price_per_m2,
          SUM(sum_price) / NULLIF(SUM(property_count), 0) AS avg_rental_price,
          AVG(sum_sale_price / NULLIF(count_sale_price, 0)) AS avg_sale_price,
          (SUM(CASE WHEN unit_type = 'APARTMENT' THEN property_count ELSE 0 END)::FLOAT / NULLIF(SUM(property_count), 0) * 100) AS pct_apartments,
          SUM(sum_area) / NULLIF(SUM(property_count), 0) AS avg_area
        FROM properties_db.main_cube.cube_rollup_mean
        ${where};
      `;
      try {
        const rows = await query<Metrics>(sql);
        if (rows.length > 0) {
          setMetrics(rows[0]);
        }
      } catch (e) {
        console.error('Failed to load metrics:', e);
      }
    })();
  }, [isReady, filters, query]);

  // Floor price bar (by floor_level)
  const [floorPriceData, setFloorPriceData] = useState<FloorPriceData[]>([]);
  React.useEffect(() => {
    if (!isReady) return;
    (async () => {
      const where = whereClause(filters as any, ['floor']);
      const sql = `
        SELECT floor_level AS unit_per_floor,
               AVG(sum_price / NULLIF(sum_area, 0)) AS median_price_per_m2,
               SUM(property_count) AS listing_count
        FROM properties_db.main_cube.cube_rollup_mean
        ${where}
        GROUP BY 1
        ORDER BY 1;
      `;
      try {
        const rows = await query<FloorPriceData>(sql);
        setFloorPriceData(rows);
      } catch { }
    })();
  }, [isReady, filters, query]);

  // Cube-based H3 data
  React.useEffect(() => {
    if (!isReady) return;
    (async () => {
      const where = whereClause(filters as any);
      const sql = `
        WITH agg AS (
          SELECT h3_7,
                 SUM(property_count) AS count,
                 AVG(sum_price / NULLIF(sum_area, 0)) AS median_price_per_m2,
                 STRING_AGG(DISTINCT bairro, ', ') AS neighborhoods
          FROM properties_db.main_cube.cube_rollup_mean
          ${where}
          GROUP BY 1
        )
        SELECT h3_7 AS h3_index_7,
               median_price_per_m2,
               percent_rank() OVER (ORDER BY median_price_per_m2 DESC) AS median_price_per_m2_percent_rank,
               count,
               neighborhoods
        FROM agg
        WHERE median_price_per_m2 IS NOT NULL AND count > 3
        ORDER BY median_price_per_m2 DESC;
      `;
      try {
        const rows = await query<H3PriceData>(sql);
        setH3Data(rows);
      } catch (error) {
        console.error('Failed to load H3 data:', error);
      }
    })();
  }, [isReady, filters, query]);

  const priceRange = useMemo(() => {
    if (globalPriceRange) {
      return globalPriceRange;
    }
    if (!h3Data.length) return { min: 0, max: 100 };

    const prices = h3Data.map(d => d.median_price_per_m2);
    return {
      min: Math.min(...prices),
      max: Math.max(...prices)
    };
  }, [globalPriceRange, h3Data]);

  // Filter data based on controls
  const filteredH3Data = useMemo(() => {
    if (!h3Data.length) {
      return [];
    }

    // Sort by price and take top percentile
    const sorted = [...h3Data].sort((a, b) => b.median_price_per_m2 - a.median_price_per_m2);
    const percentileCount = Math.ceil(sorted.length * (UPPER_PERCENTILE / 100));
    const topPercentile = sorted.slice(0, percentileCount);

    // Filter by price buckets if any are selected
    if (filters.priceRange && filters.priceRange.length > 0) {
      const filtered = topPercentile.filter(d => {
        const price = d.median_price_per_m2;
        const bucket = Math.floor(price / 10) * 10;
        return filters.priceRange!.some(b => {
          if (b >= 100) return price >= 100;
          return bucket === b;
        });
      });
      return filtered;
    }

    return topPercentile;
  }, [h3Data, filters.priceRange]);


  // Use map layers hook
  const { layers, colorScaleFunc } = useMapLayers({
    filteredH3Data,
    priceRange,
    coverage: COVERAGE,
    opacity: opacity,
    onHexHover: (object) => {
      setHoveredHex(object || null);
    },
    hoveredHex: hoveredHex
  });

  // Handle loading states
  if (dbLoading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <h2>Carregando DuckDB...</h2>
        <p>Inicializando banco de dados e carregando dados H3...</p>
      </div>
    );
  }

  if (dbError) {
    return (
      <div className="error">
        <h2>Erro no Banco de Dados</h2>
        <p>{dbError}</p>
        <p>Certifique-se de que properties_webapp.duckdb está disponível na pasta public</p>
      </div>
    );
  }

  if (neighborhoodLoading || amenityPriceLoading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <h2>Carregando Dados...</h2>
        <p>Consultando dados de preço de aluguel do DuckDB...</p>
      </div>
    );
  }

  if (neighborhoodError || amenityPriceError) {
    return (
      <div className="error">
        <h2>Erro na Consulta</h2>
        <p>{neighborhoodError || amenityPriceError}</p>
        <p>Falha ao consultar os dados de preço</p>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Header */}
      <div className="app-header">
        <h1>Imoveis residenciais em Fortaleza</h1>
        <div className="header-info">
          <button
            onClick={() => set({
              bairros: undefined,
              h3: undefined,
              unitTypes: undefined,
              bedrooms: undefined,
              bathrooms: undefined,
              parking: undefined,
              priceRange: undefined,
            })}
            style={{
              marginLeft: 12,
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid #cbd5e1',
              background: '#f8fafc',
              cursor: 'pointer'
            }}
          >
            Limpar filtros
          </button>
        </div>
        <ActiveFilterChips />
      </div>

      {/* Section 1: Métricas Principais */}
      <div className="section-header">
        <h2>Métricas Principais</h2>
      </div>
      <div className="bans-section">
        <div className="bans-grid">
          <BAN
            value={metrics ? new Intl.NumberFormat('pt-BR').format(metrics.total_properties) : '...'}
            label="Imóveis"
            loading={!metrics}
          />
          <BAN
            value={metrics ? `R$ ${Math.round(metrics.avg_price_per_m2)}` : '...'}
            label="Aluguel Médio (m²)"
            loading={!metrics}
          />
          <BAN
            value={metrics && metrics.avg_rental_price ? `R$ ${Math.round(metrics.avg_rental_price)}` : '...'}
            label="Aluguel Médio"
            loading={!metrics}
          />
          <BAN
            value={metrics ? `R$ ${formatLargeNumber(metrics.avg_sale_price)}` : '...'}
            label="Valor Médio"
            loading={!metrics}
          />
          <BAN
            value={metrics ? `${metrics.pct_apartments.toFixed(1)}%` : '...'}
            label="% de Apartamentos"
            loading={!metrics}
          />
          <BAN
            value={metrics && metrics.avg_area ? `${Math.round(metrics.avg_area)}m²` : '...'}
            label="Área Média"
            loading={!metrics}
          />
        </div>
      </div>

      {/* Section 2: Visualização em Mapa */}
      <div className="section-header">
        <h2>Visualização em Mapa</h2>
      </div>

      {/* Sidebar Toggle Button (shown on small screens) */}
      {isSmallScreen && (
        <button 
          className="sidebar-toggle" 
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir filtros"
        >
          <span>☰</span> Filtros
        </button>
      )}

      {/* Sidebar Drawer Overlay (shown on small screens when open) */}
      {isSmallScreen && sidebarOpen && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setSidebarOpen(false)}
          aria-label="Fechar filtros"
        >
          <div 
            className="sidebar-drawer" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sidebar-drawer-header">
              <h2 className="sidebar-drawer-title">Filtros</h2>
              <button 
                className="sidebar-close" 
                onClick={() => setSidebarOpen(false)}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>
            <ControlPanel />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="app-body">
        {/* Control Panel - Left Sidebar (hidden on small screens) */}
        {!isSmallScreen && <ControlPanel />}

        {/* Map Wrapper with Touch Surface */}
        <div className="map-wrapper">
          <div className="map-container">
            <DeckGL
              viewState={viewState}
              onViewStateChange={({ viewState: newViewState }) => setViewState(newViewState as any)}
              controller={true}
              layers={layers}
              width="100%"
              height="100%"
            >
              {/* Tooltip */}
              {hoveredHex && (() => {
                const percentile = 1 - hoveredHex.median_price_per_m2_percent_rank;
                const comparisonText = percentile > 0.5
                  ? `Mais caro que ${(percentile * 100).toFixed(0)}% das áreas`
                  : `Mais barato que ${((1 - percentile) * 100).toFixed(0)}% das áreas`;

                return (
                  <div className="tooltip">
                    <div className="tooltip-header">
                      <strong>Visão Geral da Área</strong>
                    </div>
                    <div className="tooltip-body">
                      <div><strong>Preço:</strong> R${hoveredHex.median_price_per_m2.toFixed(2)}/m²</div>
                      <div><strong>Imóveis:</strong> {Number(hoveredHex.count)}</div>
                      <div><strong>Bairros:</strong> {hoveredHex.neighborhoods}</div>
                      <div style={{ fontSize: '11px', color: '#cbd5e0', marginTop: '4px' }}>
                        {comparisonText}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </DeckGL>

            {/* Price Scale Legend Overlay */}
            <div className="horizontal-legend">
              <span className="legend-label">preço por m²</span>
              <div className="horizontal-legend-content">
                <span className="legend-value">R${priceRange.min.toFixed(0)}</span>
                <div
                  className="horizontal-gradient-bar"
                  style={{
                    background: `linear-gradient(to right, 
                      ${colorScaleFunc(priceRange.min)}, 
                      ${colorScaleFunc(priceRange.min + (priceRange.max - priceRange.min) * 0.25)}, 
                      ${colorScaleFunc(priceRange.min + (priceRange.max - priceRange.min) * 0.5)}, 
                      ${colorScaleFunc(priceRange.min + (priceRange.max - priceRange.min) * 0.75)}, 
                      ${colorScaleFunc(priceRange.max)})`,
                  }}
                />
                <span className="legend-value">R${priceRange.max.toFixed(0)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Painel de Análise de Mercado */}
      <div className="section-header">
        <h2>Painel de Análise de Mercado</h2>
      </div>
      <div className="charts-section">
        <div className="charts-container">
          <div className="charts-grid">
            <BarChart
              data={cubeNeighborhoods}
              colorScaleFunc={colorScaleFunc}
            />
            <DonutUnitType data={unitTypeCounts} />
            <PriceRangeBar data={priceRangeCounts} />
            <BedroomsBar data={bedroomsCounts} />
            <FloorPriceBar data={floorPriceData || []} colorScaleFunc={colorScaleFunc} />
            <AmenityPremiumChart data={(amenityPriceData || []).slice(0, 10)} />
            
            {/* Area Analysis Section */}
            <AreaDistributionBar data={areaDistribution} />
            <AreaPriceLineChart data={areaPriceData} colorScaleFunc={colorScaleFunc} />
          </div>
        </div>
      </div>


    </div>
  );
}

export default App;
